import { makeAutoObservable } from "mobx";
import { ZSpecCommon } from "src/business/specification";
import {
  createWorkflow,
  loadAllWorkflowSpecs,
  loadWorkflow,
  saveWorkflow,
} from "src/business/workflow/apiWorkflow";
import { getWorkflowDisplayName } from "src/business/workflow/getWorkflowDisplayName";
import { ZWorkflow, zWorkflowCommon } from "src/business/workflow/ZWorkflow";
import { dataParser } from "src/common/dataParser";
import { mdmBreadcrumbs } from "src/common/mdmBreadcrumbs";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";

export type TabId = "main" | "steps" | "sys";

type WFData = {
  workflow: ZWorkflow;
  specs: ZSpecCommon[];
};

export const workflowCardPageStore = makeAutoObservable({
  async init(workflowId: string) {
    this.setCurTab("main");
    this.setWorkflowId(workflowId);
    if (this.isNew) {
      this.setData({
        status: "ready",
        result: {
          workflow: createNewWorkflow(),
          specs: [],
        },
      });
    } else {
      try {
        this.setData({ status: "wait" });
        const workflow = await loadWorkflow(workflowId);
        const { catalog } = workflow;
        if (!catalog) throw Error("Не указан каталог для рабочего потока");
        const specs: ZSpecCommon[] = await loadAllWorkflowSpecs(catalog.id);
        const result = { workflow, specs };
        this.setData({ status: "ready", result });
      } catch (error) {
        this.setData({ status: "error", error });
      }
    }
  },
  hasChanges: false,
  setHasChanges(value: boolean) {
    this.hasChanges = value;
  },
  workflowId: "",
  setWorkflowId(id: string) {
    this.workflowId = id;
  },
  get isNew() {
    return !this.workflowId;
  },

  data: { status: "none" } as RemoteData<WFData>,
  setData(newData: RemoteData<WFData>) {
    this.data = newData;
  },

  get title() {
    const { isNew, data } = this;
    if (isNew) return "Новый рабочий поток";
    return data.status === "ready"
      ? getWorkflowDisplayName(data.result.workflow)
      : "...";
  },
  get crumbs() {
    return mdmBreadcrumbs([
      { type: "AdminWorkflows" },
      { type: "Name", name: this.title },
    ]);
  },

  saving: false,
  setSaving(value: boolean) {
    this.saving = value;
  },
  save(values: unknown, onSuccess: (id: string) => void) {
    const { workflowId } = this;
    const work = async () => {
      const data = dataParser(
        () => zWorkflowCommon.parse(values),
        "Form data parse",
      );
      data.steps = data.steps ?? [];
      // Нужно убрать фиктивные id для вновь созданных шагов
      data.steps.forEach((step) => {
        // eslint-disable-next-line no-param-reassign
        if (step.id.startsWith("@")) step.id = "";
        step.predecessors?.forEach((it) => {
          // eslint-disable-next-line no-param-reassign
          if (it.id.startsWith("@")) it.id = "";
        });
      });
      // Транзитные невидимые поля
      if (this.data.status === "ready") {
        data.version = this.data.result.workflow.version;
      }
      this.setSaving(true);

      const workflow: ZWorkflow = workflowId
        ? await saveWorkflow(workflowId, data)
        : await createWorkflow(data);
      if (workflowId && this.data.status === "ready") {
        this.setData({
          status: "ready",
          result: { ...this.data.result, workflow },
        });
      }
      onSuccess(workflow.id);
    };
    work()
      .finally(() => this.setSaving(false))
      .catch(onError);
  },

  get tabOptions() {
    const res: { key: TabId; label: string }[] = [
      { key: "main", label: "Общее" },
    ];
    if (!this.isNew) {
      res.push({ label: "Настройка шагов", key: "steps" });
      res.push({ label: "Системные атрибуты", key: "sys" });
    }
    return res;
  },
  curTab: "main" as TabId,
  setCurTab(tab: TabId) {
    this.curTab = tab;
  },

  get isActionsVisible(): boolean {
    return !!this.workflowId;
  },

  get canDelete(): boolean {
    return this.data.status === "ready";
  },
  get msgDelete(): string {
    const { data } = this;
    const name =
      data.status === "ready"
        ? `"${getWorkflowDisplayName(data.result.workflow)}"`
        : "";
    return `Рабочий поток ${name} будет удалён безвозможности восстановления.`;
  },
});

const createNewWorkflow = (): ZWorkflow => ({
  id: "",
  displayId: 0,
  name: "",
  displayName: "",
  roles: [],
  steps: [],
});
